import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, Spinner, WarningCircle } from "@phosphor-icons/react";
import { api, money } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function PaymentSuccess() {
  const nav = useNavigate();
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [txn, setTxn] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) { setState("error"); return; }
    let timer;
    const poll = async () => {
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        setTxn(r.data);
        if (r.data.payment_status === "paid") { setState("paid"); return; }
        if (["expired", "failed"].includes(r.data.payment_status)) { setState("error"); return; }
        if (tries.current >= 6) { setState("pending"); return; }
        tries.current += 1;
        timer = setTimeout(poll, 2000);
      } catch (e) { setState("error"); }
    };
    poll();
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center" data-testid="payment-success-page">
      {state === "checking" && (
        <>
          <Spinner size={48} className="text-orange-600 animate-spin" />
          <p className="mt-4 font-heading font-bold text-lg text-zinc-800">Confirming your payment…</p>
        </>
      )}
      {state === "paid" && (
        <>
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center" data-testid="payment-paid">
            <CheckCircle size={48} weight="fill" className="text-green-600" />
          </div>
          <h1 className="mt-5 font-heading font-black text-2xl text-zinc-900">Payment received</h1>
          <p className="text-zinc-500 mt-2">Thank you! Your payment of {money((txn?.base ?? txn?.amount) || 0)} was successful{txn?.tip > 0 ? `, plus a ${money(txn.tip)} tip` : ""}.</p>
          {txn?.share_id && <Button data-testid="view-invoice-btn" onClick={() => nav(`/share/${txn.share_id}`)} className="mt-6 h-12 px-6 font-bold">View Invoice</Button>}
        </>
      )}
      {state === "pending" && (
        <>
          <Spinner size={48} className="text-orange-600 animate-spin" />
          <h1 className="mt-5 font-heading font-black text-2xl text-zinc-900">Payment processing</h1>
          <p className="text-zinc-500 mt-2">This is taking a moment. Your invoice will update automatically once it clears.</p>
          {txn?.share_id && <Button data-testid="view-invoice-btn" variant="outline" onClick={() => nav(`/share/${txn.share_id}`)} className="mt-6 h-12 px-6 font-bold">Back to Invoice</Button>}
        </>
      )}
      {state === "error" && (
        <>
          <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center">
            <WarningCircle size={48} weight="fill" className="text-red-600" />
          </div>
          <h1 className="mt-5 font-heading font-black text-2xl text-zinc-900">Payment not completed</h1>
          <p className="text-zinc-500 mt-2">We couldn't confirm this payment. If you were charged, it will reconcile shortly.</p>
          <Button data-testid="home-btn" onClick={() => nav("/")} className="mt-6 h-12 px-6 font-bold">Go Home</Button>
        </>
      )}
    </div>
  );
}
